import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Plus, Copy } from "lucide-react";

export default function AdminUsers() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "ambassador", team_id: "" });
  const [inviteLink, setInviteLink] = useState(null);
  const [adjust, setAdjust] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ points: 0, reason: "" });

  const load = () => api.get("/users").then(({ data }) => setUsers(data));
  useEffect(() => {
    load();
    api.get("/teams").then(({ data }) => setTeams(data));
  }, []);

  const invite = async () => {
    try {
      const { data } = await api.post("/auth/invites", { ...form, team_id: form.team_id || null });
      setInviteLink(`${window.location.origin}/invite/${data.token}`);
      toast.success(t("admin.inviteCreated"));
      setForm({ email: "", name: "", role: "ambassador", team_id: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const updateUser = async (id, patch) => {
    try { await api.patch(`/users/${id}`, patch); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const submitAdjust = async () => {
    try {
      await api.post("/ledger/adjust", {
        user_id: adjust.id, points: Number(adjustForm.points) || 0, reason: adjustForm.reason,
      });
      toast.success(t("common.saved"));
      setAdjust(null); setAdjustForm({ points: 0, reason: "" });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-users-page">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-extrabold">{t("nav.users")}</h1>
        <Button data-testid="invite-user-btn" onClick={() => { setInviteLink(null); setOpen(true); }}
          className="h-12 rounded-full font-bold"><Plus className="mr-1 h-4 w-4" />{t("admin.invite")}</Button>
      </div>

      <div className="vy-card overflow-x-auto p-5">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="py-2">{t("admin.name")}</th>
              <th className="py-2">{t("admin.role")}</th>
              <th className="py-2">{t("admin.team")}</th>
              <th className="py-2">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border" data-testid={`user-row-${u.id}`}>
                <td className="py-3">
                  <p className="font-semibold">{u.name}</p>
                  <p className="text-xs text-muted-foreground">{u.email}</p>
                </td>
                <td className="py-3">
                  <select data-testid={`user-role-select-${u.id}`} value={u.role}
                    onChange={(e) => updateUser(u.id, { role: e.target.value })}
                    className="h-10 rounded-full border border-input px-3 text-xs font-semibold">
                    <option value="ambassador">{t("admin.ambassador")}</option>
                    <option value="team_leader">{t("admin.team_leader")}</option>
                    <option value="admin">{t("admin.admin")}</option>
                    {currentUser?.role === "super_admin" && (
                      <option value="super_admin">{t("admin.super_admin")}</option>
                    )}
                  </select>
                </td>
                <td className="py-3">
                  <select data-testid={`user-team-select-${u.id}`} value={u.team_id || ""}
                    onChange={(e) => updateUser(u.id, { team_id: e.target.value || null })}
                    className="h-10 rounded-full border border-input px-3 text-xs font-semibold">
                    <option value="">{t("common.none")}</option>
                    {teams.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
                  </select>
                </td>
                <td className="py-3">
                  <div className="flex flex-wrap gap-2">
                    <Button data-testid={`adjust-score-btn-${u.id}`} variant="outline"
                      className="h-10 rounded-full text-xs font-bold" onClick={() => setAdjust(u)}>
                      {t("admin.adjustScore")}
                    </Button>
                    <Button data-testid={`toggle-active-btn-${u.id}`} variant="ghost"
                      className="h-10 rounded-full text-xs font-bold"
                      onClick={() => updateUser(u.id, { is_active: !(u.is_active !== false) })}>
                      {u.is_active === false ? t("common.yes") : t("common.no")}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader><DialogTitle>{t("admin.inviteUser")}</DialogTitle></DialogHeader>
          {inviteLink ? (
            <div className="space-y-3">
              <p className="text-sm font-semibold">{t("admin.inviteLink")}</p>
              <div className="flex items-center gap-2">
                <Input data-testid="invite-link-output" readOnly value={inviteLink} className="h-12" />
                <Button variant="outline" className="h-12 rounded-full"
                  onClick={() => { navigator.clipboard?.writeText(inviteLink); toast.success(t("common.saved")); }}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <Input data-testid="invite-name-input" className="h-12" placeholder={t("admin.name")}
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Input data-testid="invite-email-input" className="h-12" type="email" placeholder={t("auth.email")}
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <select data-testid="invite-role-select" className="h-12 w-full rounded-xl border border-input px-3"
                value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="ambassador">{t("admin.ambassador")}</option>
                <option value="team_leader">{t("admin.team_leader")}</option>
                <option value="admin">{t("admin.admin")}</option>
              </select>
              <select data-testid="invite-team-select" className="h-12 w-full rounded-xl border border-input px-3"
                value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })}>
                <option value="">{t("common.none")}</option>
                {teams.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
              </select>
            </div>
          )}
          <DialogFooter>
            {!inviteLink && (
              <Button data-testid="invite-submit-btn" onClick={invite} className="h-12 rounded-full font-bold">
                {t("admin.invite")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adjust} onOpenChange={(o) => { if (!o) setAdjust(null); }}>
        <DialogContent data-testid="adjust-score-dialog">
          <DialogHeader><DialogTitle>{t("admin.manualAdjust")} — {adjust?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="adjust-points-input" type="number" className="h-12" placeholder="points"
              value={adjustForm.points} onChange={(e) => setAdjustForm({ ...adjustForm, points: e.target.value })} />
            <Input data-testid="adjust-reason-input" className="h-12" placeholder={t("common.reason")}
              value={adjustForm.reason} onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })} />
          </div>
          <DialogFooter>
            <Button data-testid="adjust-submit-btn" onClick={submitAdjust} className="h-12 rounded-full font-bold">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
